import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Clip, EmotionMood, StemSet, DialogueLine, SoulEntry, EntryTake } from '../../types';
import { DialogueTimeline } from './DialogueTimeline';
import { SoulClipsVaultDrawer } from './SoulClipsVaultDrawer';
import { SelectedLineInspector } from './SelectedLineInspector';
import type { TelemetryEntry } from './TelemetryTerminal';
import { DialogueLinesPanel } from './DialogueLinesPanel';
import type { BakerStageId } from './SoulStreamLoader';
import { StudioSectionHeader } from './StudioSectionHeader';
import { StudioVideoStage } from './StudioVideoStage';
import { PlaybackMode } from './StudioTransportBar';
import { StudioRecordBar } from './StudioRecordBar';
import { useStudioDiarization } from '../../hooks/useStudioDiarization';
import { useStudioVault } from '../../hooks/useStudioVault';
import { useStudioRecording } from '../../hooks/useStudioRecording';
import { useStudioTakes } from '../../hooks/useStudioTakes';
import { useStudioAudioGraph } from '../../hooks/useStudioAudioGraph';
import { useStudioDialogueLines } from '../../hooks/useStudioDialogueLines';
import {
  type SoulClipItem,
  sanitizeTakes,
  formatTime
} from './studioUtils';
import {
  createEntry,
  updateEntry,
  forkEntry,
  shareEntry,
  listEntries,
  fetchEntryByClipId
} from '../../services/entryService';
import { uploadUserClip, saveTempVaultClip } from '../../services/curatorService';
import { AlertCircle } from 'lucide-react';
import { useTokens } from '../../context/TokenContext';
import { applyEdgeSmoothingToBuffer } from '../../utils/audioSmoothing';

export interface SoulStudioProps {
  currentClip?: Clip | null;
  initialEntry?: SoulEntry | null;
  currentMood: EmotionMood;
  performerName?: string;
  onUpdatePerformerName?: (name: string) => void;
  onSelectClip?: (clip: Clip | null) => void;
  onOpenGallery: () => void;
  onBackToCapture: () => void;
  onEntryUpdated?: (entry: SoulEntry) => void;
  onShareCurrentClip?: (clip: Clip | SoulClipItem) => void;
}
 
export const SoulStudio: React.FC<SoulStudioProps> = ({
  currentClip,
  initialEntry = null,
  currentMood,
  performerName: propPerformerName,
  onUpdatePerformerName,
  onSelectClip,
  onOpenGallery,
  onBackToCapture,
  onEntryUpdated,
  onShareCurrentClip
}) => {
  // ──────────────────────────────────────────────
  // 1. Entry & Performer State
  // ──────────────────────────────────────────────
  const [activeEntry, setActiveEntry] = useState<SoulEntry | null>(initialEntry);

  const [performerName, setPerformerName] = useState<string>(() => {
    return propPerformerName ?? localStorage.getItem('soulsync_performer_name') ?? '';
  });
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [tempName, setTempName] = useState<string>(performerName);

  useEffect(() => {
    setPerformerName(propPerformerName || '');
    setTempName(propPerformerName || '');
  }, [propPerformerName]);

  const { refreshBalance } = useTokens();

  // ──────────────────────────────────────────────
  // 2. Playback & Video State
  // ──────────────────────────────────────────────
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(() => {
    if (initialEntry?.duration) return initialEntry.duration;
    if (!currentClip || !currentClip.id) return 0;
    return currentClip.duration || Math.max(5, currentClip.trimEnd - currentClip.trimStart);
  });
  const [playbackMode, setPlaybackMode] = useState<PlaybackMode>('mixed');
  const [isLooping, setIsLooping] = useState<boolean>(false);
  const [hasError, setHasError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const vocalAudioRef = useRef<HTMLAudioElement | null>(null);
  const bgmAudioRef = useRef<HTMLAudioElement | null>(null);
  const takeAudioRef = useRef<HTMLAudioElement | null>(null);
  const takeAuditionAudioRef = useRef<HTMLAudioElement | null>(null);
  const takeBuffersRef = useRef<Map<string, AudioBuffer>>(new Map());
  const activeTakeSourceNodeRef = useRef<AudioBufferSourceNode | null>(null);



  // ──────────────────────────────────────────────
  // 4. Stems & Cloudflare R2 Readiness State
  // ──────────────────────────────────────────────
  const [hasStems, setHasStems] = useState<boolean>(() => {
    return Boolean(
      currentClip?.isPreset ||
      (currentClip as any)?.hasStems === true ||
      (currentClip?.id && !currentClip?.pendingFile && (currentClip as any)?.hasStems !== false)
    );
  });
  const hasStemsRef = useRef<boolean>(hasStems);
  hasStemsRef.current = hasStems;
  const [telemetryLogs, setTelemetryLogs] = useState<TelemetryEntry[]>([]);

  // Telemetry logger helper
  const addTelemetryLog = useCallback((message: string, type: TelemetryEntry['type'] = 'info') => {
    const now = new Date();
    const timeStr = `${now.toTimeString().split(' ')[0]}.${String(now.getMilliseconds()).padStart(3, '0').substring(0, 2)}`;
    setTelemetryLogs((prev) => [
      ...prev.slice(-40),
      { id: `${Date.now()}-${Math.random()}`, time: timeStr, type, message }
    ]);
  }, []);

  const addTelemetryLogRef = useRef(addTelemetryLog);
  addTelemetryLogRef.current = addTelemetryLog;
  const checkClipAndStemsReadinessRef = useRef<(() => Promise<void>) | null>(null);
  const refreshSoulClipsRef = useRef<(() => void) | null>(null);

  const [r2Mp4Status, setR2Mp4Status] = useState<'pending' | 'ready' | 'checking'>('checking');
  const [transferProgress, setTransferProgress] = useState<number>(0);
  const [transferStatusText, setTransferStatusText] = useState<string>('');
  const hasMp4Ref = useRef<boolean>(false);
  hasMp4Ref.current = r2Mp4Status === 'ready';
  const [r2VocalsStatus, setR2VocalsStatus] = useState<'pending' | 'ready' | 'checking'>('checking');
  const [r2BgmStatus, setR2BgmStatus] = useState<'pending' | 'ready' | 'checking'>('checking');
  const [bakeTimeoutError, setBakeTimeoutError] = useState<string | null>(null);
  const [bakeRetryCount, setBakeRetryCount] = useState<number>(0);
  const linesListRef = useRef<HTMLDivElement | null>(null);
  const isCheckingRef = useRef<boolean>(false);

  // Ref for deferred applyVocalMuting call from audio graph
  const applyVocalMutingRef = useRef<(time: number, force?: boolean) => void>(() => {});

  // ──────────────────────────────────────────────
  // 5. Dialogue Lines Hook
  // ──────────────────────────────────────────────
  const {
    dialogueLines,
    setDialogueLines,
    dialogueLinesRef,
    selectedLineId,
    setSelectedLineId,
    selectedLineIds,
    setSelectedLineIds,
    inspectorFocus,
    setInspectorFocus,
    selectedLine,
    handleInspectLine,
    handleToggleLineSelection,
    handleSelectLine,
    handleSelectAllLines,
    handleDeselectAllLines,
    handlePrevLine,
    handleNextLine,
    handleUpdateLineCharacter,
    handleUpdateLineText,
    handleUpdateLineTiming,
    handleNudgeTime,
    handleToggleLineMute,
    handleAddDialogueLine,
    handleDeleteDialogueLine,
    handleSplitLine,
    handleCombineWithNext
  } = useStudioDialogueLines({
    currentClip,
    initialEntry,
    duration,
    activeEntry,
    videoRef,
    currentTime,
    applyVocalMuting: (time, force) => applyVocalMutingRef.current(time, force)
  });

  const [splitModeActive, setSplitModeActive] = useState(false);
  const [splitPoint, setSplitPoint] = useState(0);

  // ──────────────────────────────────────────────
  // 6. Takes & Studio Audio Graph Setup
  // ──────────────────────────────────────────────
  const takesRef = useRef<EntryTake[]>(sanitizeTakes(initialEntry?.takes || []));
  const activePlayingTakeIdRef = useRef<string | null>(null);
  const isRecordingRef = useRef<boolean>(false);
  const [isAuditioningOriginalGuide, setIsAuditioningOriginalGuide] = useState<boolean>(false);
  const [shareSuccessUrl, setShareSuccessUrl] = useState<string | null>(null);

  const playRangeTimeoutRef = useRef<any>(null);
  const auditionTimeoutRef = useRef<any>(null);
  const origAuditionAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isAuditioningOrigAudio, setIsAuditioningOrigAudio] = useState<boolean>(false);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const lastFetchedClipIdRef = useRef<string | null>(null);
  const isFetchingEntryRef = useRef<boolean>(false);

  // Ensure Entry Exists in Database
  const getOrCreateActiveEntry = useCallback(async (): Promise<SoulEntry> => {
    console.log('[getOrCreateActiveEntry] Called, activeEntry:', activeEntry?.id, 'currentClip:', currentClip?.id);
    if (activeEntry && (activeEntry as any).isPersisted !== false && activeEntry.clipId === currentClip?.id) {
      console.log('[getOrCreateActiveEntry] Returning existing matching activeEntry');
      return activeEntry;
    }
    if (!currentClip || !currentClip.id) {
      throw new Error('No clip loaded in studio to create entry.');
    }

    // Check if entry already exists in D1 for this clip before creating
    const effectiveUser = propPerformerName || performerName || undefined;
    const existing = await fetchEntryByClipId(currentClip.id, effectiveUser);
    if (existing && (existing as any).isPersisted !== false) {
      console.log('[getOrCreateActiveEntry] Found existing entry:', existing.id, 'takes:', existing.takes?.length);
      lastFetchedClipIdRef.current = `${currentClip.id}:${propPerformerName || ''}`;
      setActiveEntry(existing);
      if (onEntryUpdated) onEntryUpdated(existing);
      return existing;
    }
    console.log('[getOrCreateActiveEntry] No existing persisted entry, creating new one');

    const matchingActiveEntry = activeEntry?.clipId === currentClip.id ? activeEntry : null;
    const newEntry = await createEntry({
      title: currentClip.title || matchingActiveEntry?.title || 'SoulSync Session',
      performerName: effectiveUser || performerName || 'Artist',
      clipId: currentClip.id,
      videoUrl: currentClip.videoUrl || matchingActiveEntry?.videoUrl || `/api/media/clips/${currentClip.id}.mp4`,
      bgmUrl: currentClip.audioUrl || matchingActiveEntry?.bgmUrl || `/api/media/stems/${currentClip.id}/bgm.mp3`,
      vocalsUrl: currentClip.vocalsUrl || matchingActiveEntry?.vocalsUrl || `/api/media/stems/${currentClip.id}/vocals.mp3`,
      posterUrl: currentClip.posterUrl || matchingActiveEntry?.posterUrl || '',
      moodTag: currentMood,
      trimStart: currentClip.trimStart ?? matchingActiveEntry?.trimStart ?? 0,
      trimEnd: currentClip.trimEnd ?? matchingActiveEntry?.trimEnd ?? duration,
      duration: duration || matchingActiveEntry?.duration || 0,
      dialogueLines: (dialogueLines && dialogueLines.length > 0) ? dialogueLines : (matchingActiveEntry?.dialogueLines || [])
    });

    setActiveEntry(newEntry);
    if (onEntryUpdated) onEntryUpdated(newEntry);
    return newEntry;
  }, [activeEntry, currentClip, propPerformerName, performerName, currentMood, duration, dialogueLines, onEntryUpdated]);

  // ──────────────────────────────────────────────
  // 7. Web Audio Graph Hook
  // ──────────────────────────────────────────────
  const {
    audioCtxRef,
    bgmGainRef,
    vocalGainRef,
    takeGainRef,
    liveAnalyserNode,
    audioInputDevices,
    selectedMicDeviceId,
    setSelectedMicDeviceId,
    initWebAudioPipeline,
    setupHotMicMonitoring,
    ensureHotMic,
    checkSentenceMuted,
    applyVocalMuting,
    syncPlayback
  } = useStudioAudioGraph({
    videoRef,
    vocalAudioRef,
    bgmAudioRef,
    takeAudioRef,
    activeTakeSourceNodeRef,
    takeBuffersRef,
    activePlayingTakeIdRef,
    takesRef,
    takes: takesRef.current,
    dialogueLinesRef,
    dialogueLines,
    selectedLineIds,
    duration,
    playbackMode,
    hasStems,
    isAuditioningOriginalGuide,
    isRecordingRef
  });
  applyVocalMutingRef.current = applyVocalMuting;

  // ──────────────────────────────────────────────
  // 8. Studio Takes Management Hook
  // ──────────────────────────────────────────────
  const {
    takes,
    setTakes,
    activeAuditionTakeId,
    setActiveAuditionTakeId,
    handleNudgeTakeSync,
    handleNudgeTakeTrim,
    handleUpdateTakeTiming,
    handleUpdateTakeVolume,
    handleSetActiveTake,
    handleToggleActiveTake,
    handleDeleteTake,
    handleAuditionTake
  } = useStudioTakes({
    initialTakes: initialEntry?.takes,
    takesRef,
    duration,
    activeEntry,
    setActiveEntry,
    onEntryUpdated,
    videoRef,
    takeAuditionAudioRef,
    takeAudioRef,
    takeGainRef,
    activePlayingTakeIdRef,
    activeTakeSourceNodeRef,
    takeBuffersRef,
    currentTime,
    applyVocalMuting,
    setIsPlaying,
    syncPlayback,
    addTelemetryLog
  });

  // Colab-safe line selection helpers: ensure partner locked lines cannot be armed for recording
  const handleSelectAllAvailableLines = useCallback(() => {
    const lockedLineIds = new Set(
      takes
        .filter((t) => t.isActive !== false && (t.isLocked || t.isPartnerTake || (t.performerName && performerName && t.performerName.toLowerCase() !== performerName.toLowerCase())))
        .flatMap((t) => (t.lineIds && t.lineIds.length > 0 ? t.lineIds : [t.id]))
    );
    const availableLines = dialogueLines.filter((l) => !lockedLineIds.has(l.id));
    setSelectedLineIds(new Set(availableLines.map((l) => l.id)));
  }, [takes, performerName, dialogueLines, setSelectedLineIds]);

  const handleToggleLineSelectionSafe = useCallback((lineId: string, focus: 'user' | 'orig' = 'orig') => {
    const isLocked = takes.some(
      (t) => t.isActive !== false &&
             (t.isLocked || t.isPartnerTake || (t.performerName && performerName && t.performerName.toLowerCase() !== performerName.toLowerCase())) &&
             (t.lineIds?.includes(lineId) || t.id === lineId)
    );
    if (isLocked) {
      handleInspectLine(lineId, focus);
      return;
    }
    handleToggleLineSelection(lineId, focus);
  }, [takes, performerName, handleInspectLine, handleToggleLineSelection]);

  // ──────────────────────────────────────────────
  // 9. Studio Recording Pipeline Hook
  // ──────────────────────────────────────────────
  const {
    isRecording,
    countdown,
    isUploadingTake,
    handleStartRecording,
    handleStopRecording,
    handleSaveTakesForSelectedLines
  } = useStudioRecording({
    ensureHotMic,
    initWebAudioPipeline,
    dialogueLines,
    selectedLineId,
    selectedLineIds,
    setSelectedLineId,
    setSelectedLineIds,
    duration,
    videoRef,
    vocalAudioRef,
    bgmAudioRef,
    takeAudioRef,
    activeTakeSourceNodeRef,
    takeBuffersRef,
    takes,
    setTakes,
    takesRef,
    activeEntry,
    setActiveEntry,
    performerName,
    currentTime,
    setCurrentTime,
    setIsPlaying,
    syncPlayback,
    applyVocalMuting,
    setInspectorFocus,
    setHasError,
    addTelemetryLog,
    getOrCreateActiveEntry,
    onEntryUpdated,
    audioCtxRef,
    isRecordingRef,
    takeGainRef,
    takeAuditionAudioRef,
    activePlayingTakeIdRef
  });

  // Keep isRecordingRef in sync with reactive state transitions without premature unarming
  useEffect(() => {
    if (isRecording || countdown !== null) {
      isRecordingRef.current = true;
    }
  }, [isRecording, countdown]);

  // Sync state when initialEntry prop updates
  useEffect(() => {
    if (initialEntry) {
      setActiveEntry(initialEntry);
      if (initialEntry.dialogueLines && initialEntry.dialogueLines.length > 0) {
        const enriched = initialEntry.dialogueLines.map((l, idx) => ({
          ...l,
          id: l.id || `line-${idx + 1}`,
          rawStart: l.rawStart !== undefined ? l.rawStart : (l.start ?? l.startTime ?? 0),
          rawEnd: l.rawEnd !== undefined ? l.rawEnd : (l.end ?? l.endTime ?? duration)
        }));
        setDialogueLines(enriched);
        dialogueLinesRef.current = enriched;
      }
      // Only overwrite takes if initialEntry has a populated takes array
      if (initialEntry.takes && initialEntry.takes.length > 0) {
        setTakes(sanitizeTakes(initialEntry.takes));
      }
      if ((initialEntry as any)?.hasStems || currentClip?.isPreset) {
        setHasStems(true);
      }
      if (initialEntry.duration) {
        setDuration(initialEntry.duration);
      }
    }
  }, [initialEntry]);

  // Pre-decode takes into Web Audio buffers for instant, zero-latency, stutter-free playback with smoothed edges
  useEffect(() => {
    takes.forEach((t) => {
      if (t.audioUrl && !takeBuffersRef.current.has(t.id)) {
        fetch(t.audioUrl)
          .then((res) => (res.ok ? res.arrayBuffer() : null))
          .then((ab) => {
            if (ab && audioCtxRef.current) {
              return audioCtxRef.current.decodeAudioData(ab.slice(0));
            }
            return null;
          })
          .then((decoded) => {
            if (decoded) {
              applyEdgeSmoothingToBuffer(decoded, 30);
              takeBuffersRef.current.set(t.id, decoded);
            }
          })
          .catch(() => {});
      }
    });
  }, [takes]);



  // Check if dialogue lines are real or placeholder
  const hasRealDialogue = dialogueLines.length > 0 && dialogueLines.some((l) => {
    const txt = l.text || l.originalText || '';
    return txt.length > 0 && !txt.startsWith('Dialogue sentence #');
  });

  // Groq Whisper Diarization Hook (runs ONLY after Demucs stems are ready in R2)
  const handleDialogueLoaded = useCallback(async (enrichedDialogue: DialogueLine[]) => {
    setDialogueLines(enrichedDialogue);
    dialogueLinesRef.current = enrichedDialogue;
    setSelectedLineIds(new Set());
    // Persist the diarized lines to an entry (creating one if needed) so the
    // result survives re-opens and never triggers a full re-diarization.
    try {
      const entry = await getOrCreateActiveEntry();
      await updateEntry(entry.id, { dialogueLines: enrichedDialogue }).catch(() => {});
    } catch (err) {
      console.warn('Persist diarized dialogue notice:', err);
    }

    // Refresh balance so header token pill immediately updates after Demucs + Diarization settlement
    try {
      refreshBalance();
      addTelemetryLogRef.current?.('Capture complete! Demucs stems & Groq Whisper speech diarization verified. Token settled.', 'success');
    } catch (_) {}

    // Refresh vault to ensure dialogue count and latest entry state are updated
    try {
      refreshSoulClipsRef.current?.();
    } catch (_) {}
  }, [getOrCreateActiveEntry, refreshBalance]);

  const isSavedVaultClip = Boolean(
    currentClip?.id &&
    !currentClip?.pendingFile &&
    (currentClip?.isPreset || (currentClip as any)?.hasStems !== false)
  );

  const {
    isLoadingDialogue,
    diarizeError,
    diarizeCompleted,
    handleSkipDiarization
  } = useStudioDiarization({
    clipId: currentClip?.id || '',
    duration,
    hasStems,
    hasDialogueLines: hasRealDialogue,
    autoDiarize: !isSavedVaultClip,
    onDialogueLoaded: handleDialogueLoaded,
    addTelemetryLog
  });

  // ──────────────────────────────────────────────
  // Soul-Stream Bake Stage Machine (themed loader)
  // ──────────────────────────────────────────────
  const transferDone = r2Mp4Status === 'ready' || isSavedVaultClip;
  const splitDone = (transferDone && hasStems) || isSavedVaultClip;
  const hearDone = isSavedVaultClip || (splitDone && (hasRealDialogue || diarizeCompleted || (!isLoadingDialogue && diarizeError !== null)));
  const bakeStage: BakerStageId = !transferDone ? 'gather' : !splitDone ? 'split' : 'hear';
  const showBakeOverlay = !isSavedVaultClip && !(transferDone && splitDone && hearDone);

  // Centralized media element and take buffer purge helper
  const cleanupMediaAndBuffers = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    if (vocalAudioRef.current) {
      vocalAudioRef.current.pause();
      vocalAudioRef.current.currentTime = 0;
    }
    if (bgmAudioRef.current) {
      bgmAudioRef.current.pause();
      bgmAudioRef.current.currentTime = 0;
    }
    if (takeAudioRef.current) {
      takeAudioRef.current.pause();
      takeAudioRef.current.currentTime = 0;
    }
    if (takeAuditionAudioRef.current) {
      takeAuditionAudioRef.current.pause();
      takeAuditionAudioRef.current.currentTime = 0;
    }
    if (origAuditionAudioRef.current) {
      origAuditionAudioRef.current.pause();
      origAuditionAudioRef.current.currentTime = 0;
    }
    if (activeTakeSourceNodeRef.current) {
      try {
        activeTakeSourceNodeRef.current.stop();
      } catch (_) {}
      activeTakeSourceNodeRef.current = null;
    }
    takeBuffersRef.current.clear();
    setIsPlaying(false);
    setCurrentTime(0);
    setSelectedLineId(null);
    setSelectedLineIds(new Set());
  }, []);

  // SoulClips Vault Hook
  const {
    isVaultOpen,
    setIsVaultOpen,
    vaultClips,
    isLoadingVault,
    deletingClipId,
    vaultFilter,
    setVaultFilter,
    vaultSearch,
    setVaultSearch,
    filteredVaultClips,
    refreshSoulClips,
    handleSelectSoulClip,
    handleDeleteSoulClip
  } = useStudioVault({
    currentClip: currentClip || null,
    activeEntry,
    userName: propPerformerName || undefined,
    onSelectClip,
    onBeforeSelectClip: cleanupMediaAndBuffers,
    onEntryUpdated,
    lastFetchedClipIdRef,
    setActiveEntry,
    setDialogueLines,
    dialogueLinesRef,
    setTakes,
    setHasStems,
    setDuration,
    setPerformerName,
    addTelemetryLog
  });

  refreshSoulClipsRef.current = refreshSoulClips;

  // Synchronize Vault and local storage when 3-stage bake overlay completes (strictly once per clip)
  const lastBakedSyncedClipIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!showBakeOverlay && currentClip?.id && lastBakedSyncedClipIdRef.current !== currentClip.id) {
      lastBakedSyncedClipIdRef.current = currentClip.id;
      (currentClip as any).hasStems = true;
      if (!currentClip.vocalsUrl) {
        (currentClip as any).vocalsUrl = `/api/media/stems/${currentClip.id}/vocals.mp3`;
      }
      if (!currentClip.audioUrl) {
        (currentClip as any).audioUrl = `/api/media/stems/${currentClip.id}/bgm.mp3`;
      }
      if (!propPerformerName) {
        saveTempVaultClip({
          ...currentClip,
          hasStems: true,
          vocalsUrl: (currentClip as any).vocalsUrl,
          audioUrl: (currentClip as any).audioUrl
        });
      }
      refreshSoulClips();
    }
  }, [showBakeOverlay, currentClip?.id, refreshSoulClips, propPerformerName]);

  // Auto-populate room tools with entry & takes for currentClip if not already loaded
  useEffect(() => {
    let isCancelled = false;

    if (!currentClip?.id) {
      lastFetchedClipIdRef.current = null;
      cleanupMediaAndBuffers();
      setActiveEntry(null);
      setDialogueLines([]);
      dialogueLinesRef.current = [];
      setTakes([]);
      takesRef.current = [];
      return;
    }

    const currentKey = `${currentClip.id}:${propPerformerName || ''}`;

    // If this clip & user combination has already been fetched or populated, do NOT fetch again
    if (lastFetchedClipIdRef.current === currentKey) return;

    // Clean all previous media and buffers whenever switching to a new clip
    cleanupMediaAndBuffers();

    // If initialEntry already contains data matching this clipId with populated takes and matches user, mark as satisfied
    if (initialEntry?.clipId === currentClip.id && initialEntry.takes && initialEntry.takes.length > 0 && (!propPerformerName || initialEntry.performerName?.toLowerCase() === propPerformerName.toLowerCase())) {
      lastFetchedClipIdRef.current = currentKey;
      return;
    }

    // Default clean slate for the new clip before network fetch resolves
    if (!initialEntry || initialEntry.clipId !== currentClip.id) {
      setActiveEntry(null);
      setDialogueLines([]);
      dialogueLinesRef.current = [];
      setTakes([]);
      takesRef.current = [];
      const dur = currentClip.duration || Math.max(5, currentClip.trimEnd - currentClip.trimStart);
      setDuration(dur);
    }

    // Strict Concurrency Mutex on Network Polling
    if (isFetchingEntryRef.current) return;
    isFetchingEntryRef.current = true;
    lastFetchedClipIdRef.current = currentKey;

    fetchEntryByClipId(currentClip.id, propPerformerName || performerName || undefined)
      .then((entry) => {
        if (entry && !isCancelled) {
          setActiveEntry(entry);
          if (entry.dialogueLines && entry.dialogueLines.length > 0) {
            const enriched = entry.dialogueLines.map((l, idx) => ({
              ...l,
              id: l.id || `line-${idx + 1}`,
              rawStart: l.rawStart !== undefined ? l.rawStart : (l.start ?? l.startTime ?? 0),
              rawEnd: l.rawEnd !== undefined ? l.rawEnd : (l.end ?? l.endTime ?? duration)
            }));
            setDialogueLines(enriched);
            dialogueLinesRef.current = enriched;
          }
          if (currentClip?.isEditable === false || (entry as any)?.isEditable === false) {
            setTakes([]);
          } else if (entry.takes && entry.takes.length > 0) {
            setTakes(sanitizeTakes(entry.takes));
          } else {
            setTakes([]);
          }
          if (entry.duration) {
            setDuration(entry.duration);
          }
          if (!propPerformerName && !performerName && entry.performerName && entry.performerName !== 'Artist') {
            setPerformerName(entry.performerName);
          }
          if (onEntryUpdated) {
            onEntryUpdated(entry);
          }
          addTelemetryLog(`Populated room tools with active entry for "${entry.title}" (${entry.takes?.length || 0} takes, ${entry.dialogueLines?.length || 0} dialogue lines).`, 'success');
        } else if (!isCancelled) {
          // Explicit null entry: maintain clean slate for newly captured clip
          setActiveEntry(null);
          setDialogueLines([]);
          dialogueLinesRef.current = [];
          setTakes([]);
          takesRef.current = [];
        }
      })
      .catch((err) => {
        console.warn('Auto-populate entry notice:', err);
      })
      .finally(() => {
        isFetchingEntryRef.current = false;
      });

    return () => {
      isCancelled = true;
    };
  }, [currentClip?.id, propPerformerName, cleanupMediaAndBuffers]);

  // SoulClips Selection & Population Handler


  // Save performer name to localStorage
  const handleSavePerformerName = () => {
    if (!tempName.trim()) return;
    const clean = tempName.trim();
    setPerformerName(clean);
    localStorage.setItem('soulsync_performer_name', clean);
    setIsEditingName(false);
  };



  // ──────────────────────────────────────────────
  // Stem Readiness Check & Telemetry Polling (RULE: >= 2.5s cadence, in-flight mutex)
  // ──────────────────────────────────────────────
  const checkClipAndStemsReadiness = useCallback(async () => {
    if (isCheckingRef.current) return;
    if (!currentClip || !currentClip.id) {
      setR2Mp4Status('pending');
      setR2VocalsStatus('pending');
      setR2BgmStatus('pending');
      setHasStems(false);
      return;
    }
    // If both MP4 and Stems are already ready, stop checking
    if (hasMp4Ref.current && hasStemsRef.current) return;

    isCheckingRef.current = true;

    try {
      const clipId = currentClip.id;
      const needMp4 = hasMp4Ref.current ? '0' : '1';
      const needStems = hasStemsRef.current ? '0' : '1';

      // Consolidated status endpoint (single request, zero console 404 errors, no fallback HEAD loops)
      const res = await fetch(`/api/curator/status?clipId=${encodeURIComponent(clipId)}&needMp4=${needMp4}&needStems=${needStems}`);

      if (res.ok) {
        const data = await res.json() as any;
        if (data.success) {
          if (data.mp4Ready && !hasMp4Ref.current) {
            hasMp4Ref.current = true;
            setR2Mp4Status('ready');
            addTelemetryLogRef.current?.('Scene video clip captured and verified in R2 storage.', 'success');
          }

          if (data.stemsReady && !hasStemsRef.current) {
            hasStemsRef.current = true;
            setR2VocalsStatus('ready');
            setR2BgmStatus('ready');
            setHasStems(true);
            setBakeTimeoutError(null);
            addTelemetryLogRef.current?.('Demucs v4 stems fully isolated in R2 (vocals + bgm ready)', 'success');
          } else if (!hasStemsRef.current) {
            setR2VocalsStatus(data.vocalsReady ? 'ready' : 'pending');
            setR2BgmStatus(data.bgmReady ? 'ready' : 'pending');
          }
        }
      }
    } catch {
      // Quiet fail on transient network hiccups during polling
    } finally {
      isCheckingRef.current = false;
    }
  }, [currentClip?.id]);
  checkClipAndStemsReadinessRef.current = checkClipAndStemsReadiness;

  const handleRetryBake = useCallback(async () => {
    if (!currentClip?.id) return;
    setBakeTimeoutError(null);
    addTelemetryLog(`Retrying cloud baking for clip ${currentClip.id}...`, 'info');
    try {
      await fetch('/api/curator/bake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clipId: currentClip.id })
      });
      setBakeRetryCount((c) => c + 1);
    } catch (e: any) {
      addTelemetryLog(`Failed to re-trigger baker: ${e.message}`, 'warn');
    }
  }, [currentClip?.id, addTelemetryLog]);

  // Background Capture Ingestion: Streams pending file to R2, registers rights, and triggers Modal baker
  useEffect(() => {
    if (!currentClip || !currentClip.pendingFile) return;

    const file = currentClip.pendingFile;
    const clipId = currentClip.id;
    const rawStart = currentClip.rawTrimStart ?? 0;
    const rawEnd = currentClip.rawTrimEnd ?? currentClip.duration;
    const clipTitle = currentClip.title;

    let isMounted = true;
    setR2Mp4Status('pending');
    hasMp4Ref.current = false;
    setTransferProgress(0);
    setTransferStatusText('Verifying file integrity & registering rights...');

    (async () => {
      try {
        addTelemetryLog(`Starting background ingestion for "${clipTitle}" (${(file.size / (1024 * 1024)).toFixed(1)} MB)...`, 'info');

        await uploadUserClip({
          clipId,
          file,
          title: clipTitle,
          duration: currentClip.duration,
          trimStart: rawStart,
          trimEnd: rawEnd,
          rightsAffirmed: true,
          userName: performerName || propPerformerName || undefined,
          onProgress: (stage, pct) => {
            if (!isMounted) return;
            setTransferProgress(pct);
            if (stage === 'hashing') {
              setTransferStatusText('Verifying file integrity...');
            } else if (stage === 'registering') {
              setTransferStatusText('Registering rights affirmation in vault...');
            } else if (stage === 'uploading') {
              setTransferStatusText(`Streaming video to R2 object storage (${pct}%)...`);
            } else if (stage === 'baking') {
              setTransferStatusText('Video uploaded! Dispatching GPU baker for neural split...');
            }
          }
        });

        if (!isMounted) return;
        delete (currentClip as any).pendingFile;
        hasMp4Ref.current = true;
        setR2Mp4Status('ready');
        setTransferStatusText('Transferred to R2! Reprocessing video & isolating stems on GPU...');
        addTelemetryLog(`Video streamed to R2. Demucs neural separation & web codec transcoding dispatched.`, 'success');
      } catch (err: any) {
        if (!isMounted) return;
        console.error('Background capture ingestion failed:', err);
        setBakeTimeoutError(`Upload failed: ${err.message || 'Network error streaming to R2'}`);
        addTelemetryLog(`Capture ingest failed: ${err.message}`, 'warn');
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [currentClip?.id, currentClip?.pendingFile, performerName, propPerformerName, addTelemetryLog]);

  // Polling loop at strict 3.0s cadence (RULE 2)
  useEffect(() => {
    setBakeTimeoutError(null);
    if (!currentClip || !currentClip.id) {
      setR2Mp4Status('pending');
      hasMp4Ref.current = false;
      setR2VocalsStatus('pending');
      setR2BgmStatus('pending');
      hasStemsRef.current = false;
      setHasStems(false);
      return;
    }
    const isUpload = currentClip?.id?.startsWith('clip-upload-') || currentClip?.playbackMode === 'html5';
    if (isUpload && !currentClip?.pendingFile) {
      setR2Mp4Status('ready');
      hasMp4Ref.current = true;
    }

    const isFreshCapture = (currentClip as any)?.hasStems === false && Boolean(currentClip?.pendingFile);
    const alreadyHasStems = Boolean(
      currentClip?.isPreset ||
      (currentClip as any)?.hasStems === true ||
      (!isFreshCapture && Boolean(currentClip?.audioUrl && (currentClip as any)?.vocalsUrl)) ||
      (!isFreshCapture && currentClip?.id && !currentClip?.pendingFile && (currentClip as any)?.hasStems !== false)
    );
    setHasStems(alreadyHasStems);
    hasStemsRef.current = alreadyHasStems;
    setSelectedLineIds(new Set<string>());

    if (alreadyHasStems) {
      setR2VocalsStatus('ready');
      setR2BgmStatus('ready');
      setR2Mp4Status('ready');
      hasMp4Ref.current = true;
      hasStemsRef.current = true;
      setBakeTimeoutError(null);
      return;
    } else {
      // For freshly captured or unbaked clips, strictly reset stem status to pending
      setR2VocalsStatus('pending');
      setR2BgmStatus('pending');
      hasStemsRef.current = false;
    }

    let pollAttempts = 0;
    const interval = setInterval(() => {
      pollAttempts++;
      // Stop polling ONLY when BOTH MP4 and stems are ready
      if (hasMp4Ref.current && hasStemsRef.current) {
        clearInterval(interval);
        return;
      }
      if (pollAttempts > 40) {
        clearInterval(interval);
        setBakeTimeoutError('Vocal separation on GPU took longer than expected. Click "Retry Baking" or check back shortly.');
        addTelemetryLogRef.current?.('Vocal separation timed out waiting for R2 media stems.', 'warn');
        return;
      }
      checkClipAndStemsReadinessRef.current?.();
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [currentClip?.id, bakeRetryCount]);

  // When MP4 becomes ready, automatically load into video element
  useEffect(() => {
    if (r2Mp4Status === 'ready' && videoRef.current) {
      videoRef.current.load();
      setHasError(null);
    }
  }, [r2Mp4Status]);

  // When stems become ready, reload the audio elements so they load the newly baked files
  useEffect(() => {
    if (hasStems || isSavedVaultClip) {
      if (vocalAudioRef.current) {
        vocalAudioRef.current.load();
      }
      if (bgmAudioRef.current) {
        bgmAudioRef.current.load();
      }
      initWebAudioPipeline();
    }
  }, [hasStems, isSavedVaultClip, initWebAudioPipeline]);

  // On clip change, ensure media elements reload their fresh sources or purge previous buffers
  useEffect(() => {
    cleanupMediaAndBuffers();
    if (!currentClip || !currentClip.id) return;
    if ((r2Mp4Status === 'ready' || isSavedVaultClip) && videoRef.current) {
      videoRef.current.load();
    }
  }, [currentClip?.id, isSavedVaultClip, cleanupMediaAndBuffers]);



  // Native Video Time Update with Sentence Ducking (RULE: 15ms linear ramp)
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const cur = videoRef.current.currentTime;
    setCurrentTime(cur);

    applyVocalMuting(cur, false);

    if (bgmGainRef.current) {
      bgmGainRef.current.gain.value = 1.0;
    }

    // High-Precision Phase-Locked Loop (PLL) Audio Synchronization
    if (hasStems && isPlaying) {
      const vidCur = cur;

      // Ensure Vocal Audio is playing along with video
      if (vocalAudioRef.current) {
        if (vocalAudioRef.current.paused) {
          try { vocalAudioRef.current.currentTime = vidCur; } catch (_) {}
          vocalAudioRef.current.play().catch(() => {});
        } else {
          const drift = vocalAudioRef.current.currentTime - vidCur;
          if (Math.abs(drift) > 0.20) {
            // Large jump/drift: seek hard (RULE: resync if > 0.15s)
            vocalAudioRef.current.currentTime = vidCur;
            vocalAudioRef.current.playbackRate = 1.0;
          } else if (drift < -0.02) {
            // Audio lagging: micro-speed up by 4% to seamlessly catch up without seeking
            vocalAudioRef.current.playbackRate = 1.04;
          } else if (drift > 0.02) {
            // Audio leading: micro-slow down by 4% to seamlessly align without seeking
            vocalAudioRef.current.playbackRate = 0.96;
          } else {
            if (vocalAudioRef.current.playbackRate !== 1.0) {
              vocalAudioRef.current.playbackRate = 1.0;
            }
          }
        }
      }

      // Ensure BGM Audio is playing along with video
      if (bgmAudioRef.current) {
        if (bgmAudioRef.current.paused) {
          try { bgmAudioRef.current.currentTime = vidCur; } catch (_) {}
          bgmAudioRef.current.play().catch(() => {});
        } else {
          const drift = bgmAudioRef.current.currentTime - vidCur;
          if (Math.abs(drift) > 0.20) {
            bgmAudioRef.current.currentTime = vidCur;
            bgmAudioRef.current.playbackRate = 1.0;
          } else if (drift < -0.02) {
            bgmAudioRef.current.playbackRate = 1.04;
          } else if (drift > 0.02) {
            bgmAudioRef.current.playbackRate = 0.96;
          } else {
            if (bgmAudioRef.current.playbackRate !== 1.0) {
              bgmAudioRef.current.playbackRate = 1.0;
            }
          }
        }
      }
    }

    // High-Precision Playback of active recorded takes during playback
    const isCapturing = isRecording || isRecordingRef.current || countdown !== null;
    if (isCapturing) {
      if (takeGainRef.current && takeGainRef.current.gain.value !== 0) {
        takeGainRef.current.gain.cancelScheduledValues(0);
        takeGainRef.current.gain.value = 0.0;
      }
      if (activeTakeSourceNodeRef.current) {
        try {
          activeTakeSourceNodeRef.current.stop();
        } catch (_) {}
        activeTakeSourceNodeRef.current = null;
      }
      if (takeAudioRef.current && !takeAudioRef.current.paused) {
        takeAudioRef.current.pause();
      }
      activePlayingTakeIdRef.current = null;
    } else if (isPlaying && playbackMode !== 'original' && takes.length > 0) {
      const activeTakes = takes.filter((take) => take.isActive && cur >= take.lineStart && cur <= take.lineEnd);
      if (activeTakes.length > 0) {
        // Prioritize specific single-line takes over wide takes
        const activeTake = activeTakes.sort((a, b) => (a.duration || 999) - (b.duration || 999))[0];
        const takeAnchor = activeTake.rawStart !== undefined ? activeTake.rawStart : activeTake.lineStart;
        const expectedOffset = Math.max(0, cur - takeAnchor);
        const remainingDuration = Math.max(0, activeTake.lineEnd - cur);
        const takeVol = activeTake.volume ?? 1.0;

        if (activePlayingTakeIdRef.current !== activeTake.id) {
          activePlayingTakeIdRef.current = activeTake.id;
          if (activeTakeSourceNodeRef.current) {
            try {
              if (takeGainRef.current && audioCtxRef.current) {
                const now = audioCtxRef.current.currentTime;
                takeGainRef.current.gain.cancelScheduledValues(now);
                takeGainRef.current.gain.linearRampToValueAtTime(0.0, now + 0.015);
                activeTakeSourceNodeRef.current.stop(now + 0.015);
              } else {
                activeTakeSourceNodeRef.current.stop();
              }
            } catch (_) {}
            activeTakeSourceNodeRef.current = null;
          }

          if (takeGainRef.current && audioCtxRef.current) {
            const now = audioCtxRef.current.currentTime;
            takeGainRef.current.gain.cancelScheduledValues(now);
            // Dynamic 50ms ramp-up at in-point (including trimmed in-points!)
            takeGainRef.current.gain.setValueAtTime(0.0, now);
            takeGainRef.current.gain.linearRampToValueAtTime(takeVol, now + 0.050);

            // Dynamic 50ms ramp-down at out-point (including trimmed out-points!)
            if (remainingDuration > 0.060) {
              takeGainRef.current.gain.setValueAtTime(takeVol, now + remainingDuration - 0.050);
              takeGainRef.current.gain.linearRampToValueAtTime(0.0, now + remainingDuration);
            }
          } else if (takeGainRef.current) {
            takeGainRef.current.gain.value = takeVol;
          }

          const cachedBuffer = takeBuffersRef.current.get(activeTake.id);
          if (cachedBuffer && audioCtxRef.current && expectedOffset < cachedBuffer.duration) {
            const srcNode = audioCtxRef.current.createBufferSource();
            srcNode.buffer = cachedBuffer;
            srcNode.connect(takeGainRef.current || audioCtxRef.current.destination);
            srcNode.start(0, expectedOffset, remainingDuration);
            activeTakeSourceNodeRef.current = srcNode;
          } else if (takeAudioRef.current) {
            takeAudioRef.current.src = activeTake.audioUrl;
            takeAudioRef.current.volume = Math.min(1.0, takeVol);
            const onReady = () => {
              takeAudioRef.current?.removeEventListener('canplay', onReady);
              try {
                if (expectedOffset > 0 && expectedOffset < (takeAudioRef.current?.duration || 999)) {
                  takeAudioRef.current!.currentTime = expectedOffset;
                }
              } catch (_) {}
              takeAudioRef.current?.play().catch(() => {});
            };
            if (takeAudioRef.current.readyState >= 2) {
              onReady();
            } else {
              takeAudioRef.current.addEventListener('canplay', onReady, { once: true });
              takeAudioRef.current.load();
            }
          }
        } else {
          // If playing via fallback takeAudioRef, check drift and pause if past lineEnd
          if (!activeTakeSourceNodeRef.current && takeAudioRef.current && !takeAudioRef.current.paused) {
            if (cur >= activeTake.lineEnd) {
              takeAudioRef.current.pause();
            } else {
              const drift = Math.abs(takeAudioRef.current.currentTime - expectedOffset);
              if (drift > 0.15) {
                try { takeAudioRef.current.currentTime = expectedOffset; } catch (_) {}
              }
            }
          }
        }
      } else {
        if (activeTakeSourceNodeRef.current) {
          try {
            if (takeGainRef.current && audioCtxRef.current) {
              const now = audioCtxRef.current.currentTime;
              takeGainRef.current.gain.cancelScheduledValues(now);
              takeGainRef.current.gain.linearRampToValueAtTime(0.0, now + 0.015);
              activeTakeSourceNodeRef.current.stop(now + 0.015);
            } else {
              activeTakeSourceNodeRef.current.stop();
            }
          } catch (_) {}
          activeTakeSourceNodeRef.current = null;
        }
        if (takeAudioRef.current && !takeAudioRef.current.paused) {
          takeAudioRef.current.pause();
        }
        activePlayingTakeIdRef.current = null;
      }
    }
  };

  // Immediate vocal gain & video mute synchronization when settings change
  useEffect(() => {
    const cur = videoRef.current ? videoRef.current.currentTime : currentTime;
    applyVocalMuting(cur, true);
  }, [applyVocalMuting, currentTime]);

  const handleTogglePlay = async () => {
    if (!currentClip || !currentClip.id) return;
    if (!videoRef.current) return;
    await initWebAudioPipeline();
    setHasError(null);

    if (r2Mp4Status === 'pending') {
      setHasError('Scene video is being prepared in R2 storage. Retrying automatically...');
      return;
    }

    try {
      if (videoRef.current.paused) {
        const cur = videoRef.current.currentTime;
        if (hasStems) {
          videoRef.current.muted = true;
          if (vocalAudioRef.current) {
            try { vocalAudioRef.current.currentTime = cur; } catch (_) {}
          }
          if (bgmAudioRef.current) {
            try { bgmAudioRef.current.currentTime = cur; } catch (_) {}
          }
        } else {
          videoRef.current.muted = false;
        }
        setIsPlaying(true);
        await Promise.all([
          videoRef.current.play(),
          hasStems && vocalAudioRef.current ? vocalAudioRef.current.play().catch((e) => console.warn('Vocal play note:', e)) : Promise.resolve(),
          hasStems && bgmAudioRef.current ? bgmAudioRef.current.play().catch((e) => console.warn('BGM play note:', e)) : Promise.resolve()
        ]);
      } else {
        videoRef.current.pause();
        if (vocalAudioRef.current) vocalAudioRef.current.pause();
        if (bgmAudioRef.current) bgmAudioRef.current.pause();
        setIsPlaying(false);
        syncPlayback(false);
      }
    } catch (err: any) {
      console.warn('Direct video play note:', err);
      try {
        if (hasStems) {
          videoRef.current.muted = true;
        } else {
          videoRef.current.muted = false;
        }
        await videoRef.current.play();
        setIsPlaying(true);
        syncPlayback(true);
      } catch (e2: any) {
        if (r2Mp4Status !== 'ready') {
          setHasError('Scene video is still preparing in R2 storage. Retrying automatically...');
        } else {
          setHasError(`Playback error: ${e2.message}`);
        }
      }
    }
  };

  const handleSeek = (time: number) => {
    if (!currentClip || !currentClip.id) return;
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
    if (vocalAudioRef.current) vocalAudioRef.current.currentTime = time;
    if (bgmAudioRef.current) bgmAudioRef.current.currentTime = time;
    if (activeTakeSourceNodeRef.current) {
      try { activeTakeSourceNodeRef.current.stop(); } catch (_) {}
      activeTakeSourceNodeRef.current = null;
    }
    if (takeAudioRef.current) {
      takeAudioRef.current.pause();
    }
    activePlayingTakeIdRef.current = null;

    applyVocalMuting(time, true);

    if (isPlaying) {
      syncPlayback(true);
    }
  };

  // Clear any active audition or play range timeouts helper
  const clearPlaybackRangeTimers = useCallback(() => {
    if (auditionTimeoutRef.current) {
      clearTimeout(auditionTimeoutRef.current);
      auditionTimeoutRef.current = null;
    }
    if (playRangeTimeoutRef.current) {
      clearTimeout(playRangeTimeoutRef.current);
      playRangeTimeoutRef.current = null;
    }
  }, []);

  // Audition a specific line segment in pure audio isolation (original guide vocal only) with 2.0s padding
  const handleAuditionSegment = (start: number, end: number, padSeconds: number = 2.0) => {
    if (!currentClip || !currentClip.id) return;
    // If already auditioning, toggle off
    if (isAuditioningOrigAudio) {
      if (origAuditionAudioRef.current) {
        origAuditionAudioRef.current.pause();
      }
      clearPlaybackRangeTimers();
      setIsAuditioningOrigAudio(false);
      return;
    }

    // Stop main video & take playback to prevent overlapping sounds
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      setIsPlaying(false);
      syncPlayback(false);
    }
    if (takeAudioRef.current) {
      takeAudioRef.current.pause();
    }
    if (activeTakeSourceNodeRef.current) {
      try { activeTakeSourceNodeRef.current.stop(); } catch (_) {}
      activeTakeSourceNodeRef.current = null;
    }
    if (takeAuditionAudioRef.current) {
      takeAuditionAudioRef.current.pause();
      setActiveAuditionTakeId(null);
    }

    clearPlaybackRangeTimers();

    const paddedStart = Math.max(0, start - padSeconds);
    const paddedEnd = Math.min(duration, end + padSeconds);
    const segDurationMs = Math.max(200, (paddedEnd - paddedStart) * 1000);

    const audioEl = origAuditionAudioRef.current;
    if (audioEl) {
      setIsAuditioningOrigAudio(true);
      audioEl.src = vocalStemSrc;
      audioEl.currentTime = paddedStart;
      audioEl.volume = 1.0;
      audioEl.play().then(() => {
        auditionTimeoutRef.current = setTimeout(() => {
          if (origAuditionAudioRef.current && !origAuditionAudioRef.current.paused) {
            origAuditionAudioRef.current.pause();
          }
          setIsAuditioningOrigAudio(false);
          auditionTimeoutRef.current = null;
        }, segDurationMs);
      }).catch((err) => {
        console.warn('Original vocal audition error:', err);
        setIsAuditioningOrigAudio(false);
      });
    }
  };

  // Playback range for latest recorded take (or active line) with 2.0s head and tail padding
  const handlePlayRange = async (padSeconds: number = 2.0) => {
    if (!currentClip || !currentClip.id) return;
    if (!videoRef.current) return;
    await initWebAudioPipeline();
    clearPlaybackRangeTimers();

    // Determine target range: latest take line range, or selected line
    let rangeStart = 0;
    let rangeEnd = duration;

    if (takes.length > 0) {
      const latestTake = takes[takes.length - 1];
      const associatedLine = dialogueLines.find((l) => latestTake.lineIds?.includes(l.id));
      rangeStart = latestTake.lineStart ?? associatedLine?.start ?? 0;
      rangeEnd = latestTake.lineEnd ?? associatedLine?.end ?? duration;
    } else if (selectedLine) {
      rangeStart = selectedLine.start ?? 0;
      rangeEnd = selectedLine.end ?? duration;
    }

    const paddedStart = Math.max(0, rangeStart - padSeconds);
    const paddedEnd = Math.min(duration, rangeEnd + padSeconds);
    const segDurationMs = Math.max(200, (paddedEnd - paddedStart) * 1000);

    // If currently playing, pause first so seek settles cleanly without race conditions
    if (!videoRef.current.paused) {
      videoRef.current.pause();
    }
    if (takeAudioRef.current) takeAudioRef.current.pause();
    if (activeTakeSourceNodeRef.current) {
      try { activeTakeSourceNodeRef.current.stop(); } catch (_) {}
      activeTakeSourceNodeRef.current = null;
    }
    activePlayingTakeIdRef.current = null;

    // Direct synchronized seek to paddedStart on video and stems
    videoRef.current.currentTime = paddedStart;
    setCurrentTime(paddedStart);
    if (hasStems) {
      videoRef.current.muted = true;
      if (vocalAudioRef.current) {
        try { vocalAudioRef.current.currentTime = paddedStart; } catch (_) {}
      }
      if (bgmAudioRef.current) {
        try { bgmAudioRef.current.currentTime = paddedStart; } catch (_) {}
      }
    } else {
      videoRef.current.muted = false;
    }
    applyVocalMuting(paddedStart, true);

    // Wait for the video element to complete its seek before starting playback to eliminate start stutter
    const startPlaybackAfterSeek = async () => {
      if (!videoRef.current) return;
      try {
        setIsPlaying(true);
        await Promise.all([
          videoRef.current.play(),
          hasStems && vocalAudioRef.current ? vocalAudioRef.current.play().catch(() => {}) : Promise.resolve(),
          hasStems && bgmAudioRef.current ? bgmAudioRef.current.play().catch(() => {}) : Promise.resolve()
        ]);

        playRangeTimeoutRef.current = setTimeout(() => {
          if (videoRef.current && !videoRef.current.paused) {
            videoRef.current.pause();
            setIsPlaying(false);
            syncPlayback(false);
          }
          playRangeTimeoutRef.current = null;
        }, segDurationMs);
      } catch (err) {
        console.warn('Play range start note:', err);
      }
    };

    if (videoRef.current.seeking) {
      const onSeeked = () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('seeked', onSeeked);
        }
        startPlaybackAfterSeek();
      };
      videoRef.current.addEventListener('seeked', onSeeked, { once: true });
      // Fallback timeout in case seeked fired synchronously or was missed
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('seeked', onSeeked);
        }
        if (videoRef.current && videoRef.current.paused && !isPlaying) {
          startPlaybackAfterSeek();
        }
      }, 70);
    } else {
      // Seek was immediate
      startPlaybackAfterSeek();
    }
  };


  // ──────────────────────────────────────────────
  const handleShareEntry = async () => {
    try {
      const entry = await getOrCreateActiveEntry();
      const res = await shareEntry(entry.id);
      setShareSuccessUrl(res.shareUrl);
      navigator.clipboard.writeText(res.shareUrl).catch(() => {});
      addTelemetryLog(`Entry shared! Link copied to clipboard: ${res.shareUrl}`, 'success');
    } catch (err: any) {
      setHasError(`Failed to share: ${err.message}`);
    }
  };

  const handleForkEntry = async () => {
    if (!activeEntry) return;
    try {
      const forked = await forkEntry(activeEntry.id, performerName);
      setActiveEntry(forked);
      setTakes(sanitizeTakes(forked.takes || []));
      if (onEntryUpdated) onEntryUpdated(forked);
      addTelemetryLog(`Forked entry #${forked.id}! Ready for your takes`, 'success');
    } catch (err: any) {
      setHasError(`Failed to fork: ${err.message}`);
    }
  };

  // Format seconds to mm:ss.s
  const formatTime = (s: number) => {
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    const ms = Math.floor((s % 1) * 10);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}.${ms}`;
  };

  const videoStreamSrc = (() => {
    if (!currentClip || !currentClip.id) return '';
    const url = currentClip.videoUrl || '';
    if (url.startsWith('/demo/')) {
      return url;
    }
    // Only wait for R2 check on fresh unsaved captures actively baking
    if (!isSavedVaultClip) {
      if (r2Mp4Status !== 'ready') return '';
      if (showBakeOverlay && r2VocalsStatus !== 'ready') {
        return '';
      }
    }

    if (url.startsWith('/api/media/') || url.endsWith('.mp4') || url.endsWith('.webm')) {
      return url;
    }
    return `/api/media/clips/${currentClip.id}.mp4`;
  })();
  const appendStemCacheBuster = (url: string, id: string) => {
    if (!url) return '';
    return url.includes('?v=') ? url : `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(id)}`;
  };
  const vocalStemSrc = (currentClip?.id && (hasStems || isSavedVaultClip))
    ? appendStemCacheBuster((currentClip as any).vocalsUrl || `/api/media/stems/${currentClip.id}/vocals.mp3`, currentClip.id)
    : '';
  const bgmStemSrc = (currentClip?.id && (hasStems || isSavedVaultClip))
    ? appendStemCacheBuster((currentClip as any).bgmUrl || (currentClip as any).audioUrl || `/api/media/stems/${currentClip.id}/bgm.mp3`, currentClip.id)
    : '';

  const handleShareCurrentPerformance = () => {
    const clipToShare = currentClip || (vaultClips.length > 0 ? vaultClips[0] : null);
    if (!clipToShare) return;
    const clipWithTakes: Clip = {
      ...clipToShare,
      takes: takes.filter((t) => t.isActive),
      dialogueLines: dialogueLines.length > 0 ? dialogueLines : (clipToShare as any).dialogueLines || [],
      colabSharedClipId: activeEntry?.colabSharedClipId,
      partnerName: activeEntry?.partnerName
    } as any;
    if (onEntryUpdated && activeEntry) {
      onEntryUpdated({
        ...activeEntry,
        takes: takes.filter((t) => t.isActive),
        dialogueLines: dialogueLines.length > 0 ? dialogueLines : activeEntry.dialogueLines
      });
    }
    if (onShareCurrentClip) {
      onShareCurrentClip(clipWithTakes);
    } else {
      onOpenGallery();
    }
  };

  return (
    <div className="studio-wrapper">
      {/* ── Top Panel: SoulClips Vault (Twirl-Open Panel Taking Over Top Bar) ── */}
      <SoulClipsVaultDrawer
        isVaultOpen={isVaultOpen}
        setIsVaultOpen={setIsVaultOpen}
        vaultClips={vaultClips}
        filteredVaultClips={filteredVaultClips}
        currentClip={currentClip || null}
        duration={duration}
        takes={takes}
        dialogueLines={dialogueLines}
        isLoadingVault={isLoadingVault}
        deletingClipId={deletingClipId}
        vaultFilter={vaultFilter}
        setVaultFilter={setVaultFilter}
        vaultSearch={vaultSearch}
        setVaultSearch={setVaultSearch}
        refreshSoulClips={refreshSoulClips}
        handleSelectSoulClip={handleSelectSoulClip}
        handleDeleteSoulClip={handleDeleteSoulClip}
        userName={propPerformerName || undefined}
        onBackToCapture={onBackToCapture}
        onShareClipToSoulShare={(clipItem) => {
          if (onShareCurrentClip) {
            onShareCurrentClip(clipItem);
          } else {
            onOpenGallery();
          }
        }}
      />

      {/* ── Temp Vault Banner (shown for unlogged sessions) ── */}
      {!propPerformerName && (
        <div
          style={{
            padding: '0.45rem 1.25rem',
            background: 'linear-gradient(90deg, rgba(234, 179, 8, 0.12) 0%, rgba(139, 92, 246, 0.1) 100%)',
            borderBottom: '1px solid rgba(234, 179, 8, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.78rem',
            color: '#fef08a'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>⚡</span>
            <span>
              <strong>Temp Vault Active:</strong> Captured scenes and takes are stored in your temporary browser session. Sign In at the top right anytime to make them persistent.
            </span>
          </div>
        </div>
      )}

      {/* ── Error Banner ── */}
      {hasError && (
        <div style={{ padding: '0.75rem 1rem', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.35)', borderRadius: 'var(--radius-md)', color: '#fca5a5', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertCircle size={16} color="#f87171" />
            <span>{hasError}</span>
          </div>
          <button
            type="button"
            onClick={() => setHasError(null)}
            style={{ color: '#f87171', fontWeight: 'bold' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────── */}
      {/* SECTION 1: VIDEO & STEM ENGINE                               */}
      {/* ──────────────────────────────────────────────────────────── */}
      <section className="studio-section">
        {/* Section 1 Header: Video & Stems Engine */}
        <StudioSectionHeader
          activeEntry={activeEntry}
          shareSuccessUrl={null}
          onForkEntry={handleForkEntry}
          onShareEntry={handleShareCurrentPerformance}
          onDismissShareSuccess={() => setShareSuccessUrl(null)}
          audioInputDevices={audioInputDevices}
          selectedMicDeviceId={selectedMicDeviceId}
          onSelectMicDevice={setSelectedMicDeviceId}
        />

        {/* Master Stage Grid: Video Player on Left, Interactive Dialogue Lines Panel on Right */}
        <div className="studio-stage-grid">
          {/* Video Player Display */}
          <StudioVideoStage
            videoContainerRef={videoContainerRef}
            videoRef={videoRef}
            vocalAudioRef={vocalAudioRef}
            bgmAudioRef={bgmAudioRef}
            takeAudioRef={takeAudioRef}
            takeAuditionAudioRef={takeAuditionAudioRef}
            origAuditionAudioRef={origAuditionAudioRef}
            videoStreamSrc={videoStreamSrc}
            vocalStemSrc={vocalStemSrc}
            bgmStemSrc={bgmStemSrc}
            posterUrl={currentClip?.posterUrl || ''}
            hasStems={Boolean(hasStems || isSavedVaultClip)}
            hasClip={Boolean(currentClip && currentClip.id)}
            onOpenVault={() => setIsVaultOpen(true)}
            onBackToCapture={onBackToCapture}
            showBakeOverlay={showBakeOverlay}
            bakeStage={bakeStage}
            clipTitle={currentClip?.title || currentClip?.id || ''}
            transferDone={transferDone}
            transferStatusText={transferStatusText}
            transferProgress={transferProgress}
            splitDone={splitDone}
            hearDone={hearDone}
            isLoadingDialogue={isLoadingDialogue}
            bakeTimeoutError={bakeTimeoutError}
            onRetryBake={handleRetryBake}
            onSkipHear={handleSkipDiarization}
            isPlaying={isPlaying}
            countdown={countdown}
            isRecording={isRecording}
            currentTime={currentTime}
            duration={duration}
            hasTakes={takes.length > 0}
            playbackMode={playbackMode}
            onTimeUpdate={handleTimeUpdate}
            onPlay={() => syncPlayback(true)}
            onPause={() => {
              clearPlaybackRangeTimers();
              setIsAuditioningOriginalGuide(false);
              setIsPlaying(false);
              syncPlayback(false);
            }}
            onWaiting={() => {
              if (vocalAudioRef.current) vocalAudioRef.current.pause();
              if (bgmAudioRef.current) bgmAudioRef.current.pause();
            }}
            onPlaying={() => {
              if (hasStems) {
                const cur = videoRef.current ? videoRef.current.currentTime : 0;
                if (vocalAudioRef.current) {
                  vocalAudioRef.current.currentTime = cur;
                  vocalAudioRef.current.play().catch(() => {});
                }
                if (bgmAudioRef.current) {
                  bgmAudioRef.current.currentTime = cur;
                  bgmAudioRef.current.play().catch(() => {});
                }
              }
            }}
            onEnded={() => {
              clearPlaybackRangeTimers();
              setIsAuditioningOriginalGuide(false);
              setIsPlaying(false);
              syncPlayback(false);
            }}
            onTogglePlay={handleTogglePlay}
            onPlayRange={handlePlayRange}
            onSeek={handleSeek}
            onSetPlaybackMode={setPlaybackMode}
            onTakeAuditionEnded={() => setActiveAuditionTakeId(null)}
            onOrigAuditionEnded={() => setIsAuditioningOrigAudio(false)}
            formatTime={formatTime}
          />

          {/* Dialogue Lines Script & Teleprompter Panel beside Video */}
          <DialogueLinesPanel
            dialogueLines={dialogueLines}
            currentTime={currentTime}
            duration={duration}
            selectedLineId={selectedLineId}
            selectedLineIds={selectedLineIds}
            isPlaying={isPlaying}
            isRecording={isRecording}
            linesListRef={linesListRef}
            takes={takes}
            currentPerformer={performerName}
            partnerName={activeEntry?.partnerName}
            onSelectLine={(lineId, focus) => handleInspectLine(lineId, focus)}
            onToggleSelectLine={(lineId, focus) => handleToggleLineSelectionSafe(lineId, focus)}
            onSelectAllLines={handleSelectAllAvailableLines}
            onDeselectAllLines={handleDeselectAllLines}
            onSeek={handleSeek}
            onAuditionSegment={handleAuditionSegment}
          />

          {/* Red Recording Button and Mic Response Waveform Window */}
          <StudioRecordBar
            isRecording={isRecording}
            countdown={countdown}
            isUploadingTake={isUploadingTake}
            hasSelectedLines={selectedLineIds.size > 0}
            liveAnalyserNode={selectedLineIds.size > 0 ? liveAnalyserNode : null}
            onStartRecording={handleStartRecording}
            onStopRecording={handleStopRecording}
            onSetupHotMicMonitoring={setupHotMicMonitoring}
          />
        </div>

        {/* Draggable Multi-Layer Dialogue Timeline Embedded Directly Below Video */}
        <DialogueTimeline
          duration={duration}
          currentTime={currentTime}
          dialogueLines={dialogueLines}
          takes={takes}
          currentPerformer={performerName}
          selectedLineId={selectedLineId}
          selectedLineIds={selectedLineIds}
          auditioningTakeId={activeAuditionTakeId}
          onSelectLine={handleInspectLine}
          onUpdateLineTiming={handleUpdateLineTiming}
          onUpdateTakeTiming={handleUpdateTakeTiming}
          onSeek={handleSeek}
          onAuditionSegment={handleAuditionSegment}
          onAuditionTake={handleAuditionTake}
          onNudgeTakeSync={handleNudgeTakeSync}
          isPlaying={isPlaying}
        />

        {/* FUSED: Selected Line Options & Takes Inspector (inside Video & Stems box) */}
        <SelectedLineInspector
          dialogueLines={dialogueLines}
          selectedLine={selectedLine || null}
          selectedLineIds={selectedLineIds}
          takes={takes}
          duration={duration}
          inspectorFocus={inspectorFocus}
          setInspectorFocus={setInspectorFocus}
          activeAuditionTakeId={activeAuditionTakeId}
          isAuditioningOrigAudio={isAuditioningOrigAudio}
          diarizeError={diarizeError}
          handlePrevLine={handlePrevLine}
          handleNextLine={handleNextLine}
          handleSelectAllLines={handleSelectAllLines}
          handleDeselectAllLines={handleDeselectAllLines}
          handleToggleLineSelection={handleToggleLineSelection}
          handleNudgeTakeTrim={handleNudgeTakeTrim}
          handleNudgeTakeSync={handleNudgeTakeSync}
          handleUpdateTakeVolume={handleUpdateTakeVolume}
          handleAuditionTake={handleAuditionTake}
          handleSetActiveTake={handleSetActiveTake}
          handleToggleActiveTake={handleToggleActiveTake}
          handleDeleteTake={handleDeleteTake}
          handleAuditionSegment={handleAuditionSegment}
          handleNudgeTime={handleNudgeTime}
          handleUpdateLineText={handleUpdateLineText}
          handleSplitLine={handleSplitLine}
          handleCombineWithNext={handleCombineWithNext}
          splitModeActive={splitModeActive}
          setSplitModeActive={setSplitModeActive}
          splitPoint={splitPoint}
          setSplitPoint={setSplitPoint}
        />
      </section>

      </div>
  );
};
